import { useCallback } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { getListingDetailsQueryKey } from '@zg-rentals/zillow-rental-manager/hooks/useGetListingDetails';

import { AMENITY_LOCATION_BY_FIELD, LAUNDRY_VALUE_BY_FIELD } from '../../constants/KeyFeaturesConstants';

import type {
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  GetUserPropertyDetailsCustomAmenityWebDto,
  GetUserPropertyDetailsFloorPlanUnitWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsResponseWebDto,
  GetUserPropertyDetailsRoomForRentWebDto,
  RentalManagerModularListingProgressV2WebDto,
  UserPropertyEditPhoneResponseV2WebDto,
  UserPropertyEditPhoneV2RequestDto,
  UserPropertyMultiEditFloorPlanWebDto,
  MultiErrorsResponseDto,
  UserPropertyDetailsSpecialOfferDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { AmenityLocation } from '../../constants/KeyFeaturesConstants';

// After a save, these helpers write the saved value into the cached listing, so the page updates
// before the refetch comes back.

// `field` is the name the save sent, for example 'squareFeet'. If `/multi` rejects that field,
// its patch is skipped.
export type ListingDetailsPatch = {
  field: string;
  apply: (cached: GetUserPropertyDetailsResponseWebDto) => GetUserPropertyDetailsResponseWebDto;
};

// What `applyToCache: true` uses. It copies each saved field onto the listing as is, for example
// { hidePhone: true } sets `hidePhone`. Fields the listing does not have are skipped.
export const flatListingDetailsPatches = (variables: unknown): Array<ListingDetailsPatch> => {
  const postData = (variables as { postData?: Record<string, unknown> } | undefined)?.postData;

  if (!postData) {
    return [];
  }

  return Object.entries(postData)
    .filter(([field]) => field !== 'listingAlias')
    .map(([field, value]) => ({
      field,
      apply: (cached: GetUserPropertyDetailsResponseWebDto) =>
        field in cached ? { ...cached, [field]: value } : cached,
    }));
};

type ArrayFieldOf<T> = { [K in keyof T]: T[K] extends Array<unknown> ? K : never }[keyof T];
type ElementOf<T> = T extends Array<infer E> ? E : never;

// Sets one top-level field, for when the cached value differs from the value sent to the server.
export const setListingField = <K extends keyof GetUserPropertyDetailsResponseWebDto>(
  field: K,
  // undefined when the save removes the value, for example a deleted logo, because `/details` then
  // leaves the field out
  value: GetUserPropertyDetailsResponseWebDto[K] | undefined,
): ListingDetailsPatch => ({
  field,
  apply: (cached) => ({ ...cached, [field]: value }),
});

// Like setListingField, but builds the new value from the cached one, for example changing only
// `catsDto` inside `petsAllowed`. Reading the cached value, not the one the component rendered,
// keeps a slow save from undoing a newer one.
export const updateListingField = <K extends keyof GetUserPropertyDetailsResponseWebDto>(
  field: K,
  update: (current: GetUserPropertyDetailsResponseWebDto[K]) => GetUserPropertyDetailsResponseWebDto[K],
): ListingDetailsPatch => ({
  field,
  apply: (cached) => ({ ...cached, [field]: update(cached[field]) }),
});

// Sets one field on a floor plan, for example `squareFeet`. No matching floor plan means the
// caller passed the wrong id, so it logs an error.
export const setFloorPlanField = <K extends keyof GetUserPropertyDetailsFloorPlanWebDto>(
  floorPlanId: string | undefined,
  field: K,
  // null when the user clears the field
  value: GetUserPropertyDetailsFloorPlanWebDto[K] | null,
): ListingDetailsPatch => ({
  field,
  apply: (cached) => {
    if (!cached.floorPlans?.some((floorPlan) => floorPlan.id === floorPlanId)) {
      logError({
        error: new Error('No floor plan matched the id being patched'),
        context: { details: 'setFloorPlanField', field },
      });
      return cached;
    }

    return {
      ...cached,
      floorPlans: cached.floorPlans.map((floorPlan) =>
        floorPlan.id === floorPlanId ? { ...floorPlan, [field]: value } : floorPlan,
      ),
    };
  },
});

// Like setFloorPlanField, but builds the new value from the cached one, for example adding an
// amenity to `unitAmenities`.
export const updateFloorPlanField = <K extends keyof GetUserPropertyDetailsFloorPlanWebDto>(
  floorPlanId: string | undefined,
  field: K,
  update: (current: GetUserPropertyDetailsFloorPlanWebDto[K]) => GetUserPropertyDetailsFloorPlanWebDto[K],
): ListingDetailsPatch => ({
  field,
  apply: (cached) => {
    if (!cached.floorPlans?.some((floorPlan) => floorPlan.id === floorPlanId)) {
      logError({
        error: new Error('No floor plan matched the id being patched'),
        context: { details: 'updateFloorPlanField', field },
      });
      return cached;
    }

    return {
      ...cached,
      floorPlans: cached.floorPlans.map((floorPlan) =>
        floorPlan.id === floorPlanId ? { ...floorPlan, [field]: update(floorPlan[field]) } : floorPlan,
      ),
    };
  },
});

// The unit amenity delete sends only the amenity id, so this removes it from whichever floor plan
// holds it.
export const removeUnitAmenity = (amenityId: string): ListingDetailsPatch => ({
  field: 'unitAmenities',
  apply: (cached) => ({
    ...cached,
    floorPlans: cached.floorPlans?.map((floorPlan) => ({
      ...floorPlan,
      unitAmenities: floorPlan.unitAmenities?.filter((amenity) => amenity.id !== amenityId),
    })),
  }),
});

// Sets one field on a unit. A unit is under `floorPlans[].units`, or under `units` when the
// listing has no floor plans.
export const setUnitField = <K extends keyof GetUserPropertyDetailsFloorPlanUnitWebDto>(
  unitId: string | undefined,
  field: K,
  value: GetUserPropertyDetailsFloorPlanUnitWebDto[K],
): ListingDetailsPatch => ({
  field,
  apply: (cached) => {
    const holdsUnit = (units?: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>) =>
      Boolean(units?.some((unit) => unit.id === unitId));
    const setOnUnits = (units: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>) =>
      units.map((unit) => (unit.id === unitId ? { ...unit, [field]: value } : unit));

    if (!cached.floorPlans?.some((floorPlan) => holdsUnit(floorPlan.units)) && !holdsUnit(cached.units)) {
      logError({
        error: new Error('No unit matched the id being patched'),
        context: { details: 'setUnitField', field },
      });
      return cached;
    }

    return {
      ...cached,
      floorPlans: cached.floorPlans?.map((floorPlan) =>
        holdsUnit(floorPlan.units) ? { ...floorPlan, units: setOnUnits(floorPlan.units) } : floorPlan,
      ),
      units: holdsUnit(cached.units) ? setOnUnits(cached.units) : cached.units,
    };
  },
});

// MF listings come back without `amenities`, even though the type says it is always there, so
// the helpers below still check for it.
type Amenities = GetUserPropertyDetailsResponseWebDto['amenities'];
type BuildingAttributes = GetUserPropertyDetailsResponseWebDto['buildingAttributes'];

// True when an SF amenity group lists this field, for example 'hasForcedAirHeatingSystem' under
// Heating. MF listings have no groups.
const hasGroupEntry = (amenities: Amenities, fieldName: string) =>
  [...(amenities?.interior ?? []), ...(amenities?.property ?? [])].some((group) =>
    group.values?.some((value) => value.fieldName === fieldName),
  );

// Checks or unchecks one field in the SF amenity groups.
const selectInGroups = (amenities: Amenities, fieldName: string, isSelected: boolean): Amenities => {
  const selectIn = (groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto> = []) =>
    groups.map((group) => ({
      ...group,
      values: (group.values ?? []).map((value) => (value.fieldName === fieldName ? { ...value, isSelected } : value)),
    }));

  return amenities && { ...amenities, interior: selectIn(amenities.interior), property: selectIn(amenities.property) };
};

// For example, hasElevator goes to `buildingAttributes.hasElevator`, and hasCentralAirConditioning
// goes to `buildingAttributes.airConditioningTypes.hasCentral`.
const setAttribute = (
  attributes: BuildingAttributes,
  { key, category }: AmenityLocation,
  isSelected: boolean,
): BuildingAttributes =>
  category
    ? {
        ...attributes,
        [category]: { ...(attributes[category as keyof BuildingAttributes] as object), [key]: isSelected },
      }
    : { ...attributes, [key]: isSelected };

// Laundry is one choice out of four, for example picking hasInUnitLaundry sets `laundry` to
// 'In Unit', checks that option in the group, and unchecks the other three.
const selectLaundry = (
  cached: GetUserPropertyDetailsResponseWebDto,
  fieldName: string,
): GetUserPropertyDetailsResponseWebDto => ({
  ...cached,
  laundry: LAUNDRY_VALUE_BY_FIELD[fieldName],
  amenities: Object.keys(LAUNDRY_VALUE_BY_FIELD).reduce(
    (amenities, flag) => selectInGroups(amenities, flag, flag === fieldName),
    cached.amenities,
  ),
});

// Saves one amenity checkbox. MF stores it in `buildingAttributes` and SF stores it in the amenity
// groups, so it writes whichever the listing has. The saved name and the cached name often differ,
// so AMENITY_LOCATION_BY_FIELD says where each one goes in `buildingAttributes`.
// Unpicking a laundry option does nothing, because picking the new option already replaced it.
export const setAmenityField = (fieldName: string, isSelected: boolean): ListingDetailsPatch => ({
  field: fieldName,
  apply: (cached) => {
    if (LAUNDRY_VALUE_BY_FIELD[fieldName]) {
      return isSelected ? selectLaundry(cached, fieldName) : cached;
    }

    const location = AMENITY_LOCATION_BY_FIELD[fieldName];
    const inAttributes = Boolean(location && cached.buildingAttributes);
    const inGroups = hasGroupEntry(cached.amenities, fieldName);

    if (!inAttributes && !inGroups) {
      logError({
        error: new Error('No amenity location matched the field being patched'),
        context: { details: 'setAmenityField', fieldName },
      });
      return cached;
    }

    return {
      ...cached,
      buildingAttributes: inAttributes
        ? setAttribute(cached.buildingAttributes, location, isSelected)
        : cached.buildingAttributes,
      amenities: inGroups ? selectInGroups(cached.amenities, fieldName, isSelected) : cached.amenities,
    };
  },
});

// Turns an amenity save into patches, for example { hasElevator: true, listingAlias } becomes
// setAmenityField('hasElevator', true).
export const amenityPatches = (fields: object): Array<ListingDetailsPatch> =>
  Object.entries(fields)
    .filter(([field]) => field !== 'listingAlias')
    .map(([field, value]) => setAmenityField(field, Boolean(value)));

// Removes an item from a list by id, for example a deleted amenity from `apartmentAmenities`.
export const removeListingDetailsItem = <
  K extends ArrayFieldOf<GetUserPropertyDetailsResponseWebDto>,
  I extends keyof ElementOf<GetUserPropertyDetailsResponseWebDto[K]>,
>(
  field: K,
  idKey: I,
  id: ElementOf<GetUserPropertyDetailsResponseWebDto[K]>[I],
): ListingDetailsPatch => ({
  field: String(field),
  apply: (cached) => {
    const items = cached[field] as Array<ElementOf<GetUserPropertyDetailsResponseWebDto[K]>> | undefined;

    return items ? { ...cached, [field]: items.filter((item) => item[idKey] !== id) } : cached;
  },
});

// Adds an item to the end of a list, for example a new amenity to `apartmentAmenities`.
export const appendListingDetailsItem = <K extends ArrayFieldOf<GetUserPropertyDetailsResponseWebDto>>(
  field: K,
  item: ElementOf<GetUserPropertyDetailsResponseWebDto[K]>,
): ListingDetailsPatch => ({
  field: String(field),
  apply: (cached) => {
    const items = cached[field] as Array<ElementOf<GetUserPropertyDetailsResponseWebDto[K]>> | undefined;

    return items ? { ...cached, [field]: [...items, item] } : cached;
  },
});

// A special offer create adds the saved offer, and an edit replaces the offer with the same
// `offerIdEncoded`. A 200 response can still carry validation errors, and then nothing was saved.
export const specialOfferPatches = (
  isEdit: boolean,
  { errors, result }: MultiErrorsResponseDto<UserPropertyDetailsSpecialOfferDto>,
): Array<ListingDetailsPatch> => {
  if (errors?.length) {
    return [];
  }

  return [
    isEdit
      ? updateListingField(
          'specialOffers',
          (current) =>
            current && current.map((offer) => (offer.offerIdEncoded === result.offerIdEncoded ? result : offer)),
        )
      : appendListingDetailsItem('specialOffers', result),
  ];
};

type CustomAmenities = Array<GetUserPropertyDetailsCustomAmenityWebDto>;

// SF custom amenities are stored twice, in `propertyAmenities` and `amenities.additional`, so
// this updates both.
export const updatePropertyAmenities = (update: (items: CustomAmenities) => CustomAmenities): ListingDetailsPatch => ({
  field: 'propertyAmenities',
  apply: (cached) => ({
    ...cached,
    propertyAmenities: cached.propertyAmenities && update(cached.propertyAmenities),
    amenities: cached.amenities && { ...cached.amenities, additional: update(cached.amenities.additional ?? []) },
  }),
});

type RoomForRent = GetUserPropertyDetailsRoomForRentWebDto;

// The nested objects inside roomForRent, for example 'currentPets'.
type RoomForRentGroup = {
  [K in keyof RoomForRent]: RoomForRent[K] extends Array<unknown> ? never : RoomForRent[K] extends object ? K : never;
}[keyof RoomForRent];

// The save sends room fields flat, and the listing groups them, so the caller names the group. For
// example { currentCatsCount: '2' } with 'currentPets' goes to roomForRent.currentPets.currentCatsCount.
// With no group, fields go directly on roomForRent. Each patch is named after its field, so a `/multi`
// rejection of that field drops it.
export const updateRoomForRentGroup = (
  group: RoomForRentGroup | null,
  fields: object = {},
): Array<ListingDetailsPatch> =>
  Object.entries(fields).map(([field, value]) => ({
    field,
    apply: (cached) => {
      const current = cached.roomForRent;
      if (!current) {
        logError({
          error: new Error('The listing has no roomForRent to patch'),
          context: { details: 'updateRoomForRentGroup', field },
        });
        return cached;
      }

      return {
        ...cached,
        roomForRent: group
          ? { ...current, [group]: { ...current[group], [field]: value } }
          : { ...current, [field]: value },
      };
    },
  }));

// Floor plan room fields have the same names in the save and in the listing.
export const updateFloorPlanRoomForRent = (
  floorPlans: Array<UserPropertyMultiEditFloorPlanWebDto> = [],
): Array<ListingDetailsPatch> =>
  floorPlans.flatMap(({ id, roomForRent }) =>
    Object.entries(roomForRent ?? {}).map(([field, value]) => ({
      field,
      apply: (cached) => {
        if (!cached.floorPlans?.some((floorPlan) => floorPlan.id === id && floorPlan.roomForRent)) {
          logError({
            error: new Error('No floor plan with roomForRent matched the id being patched'),
            context: { details: 'updateFloorPlanRoomForRent', field },
          });
          return cached;
        }

        return {
          ...cached,
          floorPlans: cached.floorPlans.map((floorPlan) =>
            floorPlan.id === id && floorPlan.roomForRent
              ? { ...floorPlan, roomForRent: { ...floorPlan.roomForRent, [field]: value } }
              : floorPlan,
          ),
        };
      },
    })),
  );

// The fallbacks match what `AddEditPhoneNumber` shows when the response leaves a value out.
export const phonePatches = (
  { postData }: { postData: UserPropertyEditPhoneV2RequestDto },
  data: UserPropertyEditPhoneResponseV2WebDto,
): Array<ListingDetailsPatch> => [
  setListingField('phone', data.contactPhone ?? postData.phone),
  setListingField('phoneVerifiedOnMs', data.verifiedMs || Date.now()),
];

// Wizard steps save without an await, so responses can arrive out of order. An older response
// does not replace newer progress.
export const setModularListingProgress = ({
  hasReachedReview,
  step,
  updatedTimestamp,
}: RentalManagerModularListingProgressV2WebDto): ListingDetailsPatch =>
  updateListingField('modularListingProgress', (current) =>
    current && current.updatedTimestamp > updatedTimestamp ? current : { hasReachedReview, step, updatedTimestamp },
  );

// Waits for a `/details` refetch that is already running, for example the one a mutation's
// `meta.invalidates` starts. `cancelRefetch: false` joins that fetch and does not send a second request.
// If no refetch runs, it returns at once.
export const useAwaitListingDetailsRefetch = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (listingAlias: string) => {
      const queryKey = getListingDetailsQueryKey(listingAlias);

      if (!listingAlias || queryClient.isFetching({ queryKey }) === 0) {
        return;
      }

      await queryClient.refetchQueries({ queryKey }, { cancelRefetch: false });
    },
    [queryClient],
  );
};

export const useApplyListingDetailsPatches = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (listingAlias: string, patches: Array<ListingDetailsPatch>) => {
      if (!listingAlias || patches.length === 0) {
        return;
      }

      queryClient.setQueryData<GetUserPropertyDetailsResponseWebDto>(
        getListingDetailsQueryKey(listingAlias),
        // If the listing is not loaded yet, do nothing. Otherwise the cache would hold a listing
        // with only the patched fields.
        (cached) => (cached ? patches.reduce((next, patch) => patch.apply(next), cached) : cached),
      );
    },
    [queryClient],
  );
};
